import { z } from "zod";

/* ──────────────────────────────────────────────────────────────────────────
 * Tracker waitlist form schema.
 *
 * Shared between the client form (react-hook-form resolver) and the server
 * action (revalidation). The use-case IDs are stable analytics keys; the
 * labels can be re-worded without breaking historical PostHog data.
 *
 * Slice 12 (second pass): the checkbox is now a "tell us which features
 * matter most so we build yours first" prioritization signal. Some options
 * overlap with the established features shown above the form — that's fine,
 * the ask is which to build first, not which exist.
 *
 * Order matters: leading options should be the most enticing so the eye
 * lands on those first. ROC / T3 stays at the bottom — anyone who knows
 * what it means will check it; the rest will skip it without friction.
 *
 * Historical PostHog events from earlier IDs keep their old IDs (immutable
 * event log). New submissions emit the IDs below.
 * ────────────────────────────────────────────────────────────────────────── */

export const USE_CASES = [
  {
    id: "account_linking",
    label: "Direct account linking (automatic sync)",
  },
  { id: "csv_import", label: "CSV import from my brokerage" },
  { id: "audit_binder", label: "Audit binder for CRA defense" },
  {
    id: "year_end_report",
    label: "Year-end report for my accountant",
  },
  { id: "monthly_summary", label: "Monthly performance summary by email" },
  { id: "multi_leverage", label: "Tracking multiple leverage sources at once" },
  { id: "roc_t3_reconciliation", label: "ROC / T3 reconciliation" },
] as const;

export type UseCaseId = (typeof USE_CASES)[number]["id"];

export const useCaseIdSchema = z.enum([
  "account_linking",
  "csv_import",
  "audit_binder",
  "year_end_report",
  "monthly_summary",
  "multi_leverage",
  "roc_t3_reconciliation",
]);

export const waitlistInputSchema = z.object({
  email: z.string().min(1, "Email is required").email("That doesn't look like a valid email"),
  // No min — checkbox group is optional, used as feature-vote signal.
  // Submitting with none ticked is fine ("just put me on the list").
  use_cases: z.array(useCaseIdSchema),
  free_form: z.string().max(2000, "Keep it under 2000 characters").optional(),
  consent_explicit: z
    .boolean()
    .refine((v) => v === true, { message: "Consent is required to join the waitlist" }),
});

export type WaitlistInput = z.infer<typeof waitlistInputSchema>;
